import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { api, formatError } from "@/lib/api";
import {
  PLAZOS, ajustesPorCorregir, avisoPlazos, cuantos, descargarBlob, errorDeBlob, lineaAcceso, resumenPurga,
} from "@/lib/privacidad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const inputCls = "mt-1 bg-[#F7F5F0] border-[#DCD8D0]";

// A quién se busca para exportar: el parámetro de `/admin/privacy/export`, su nombre y un ejemplo inventado.
const BUSQUEDAS = {
  email: { nombre: "Correo del cliente", ejemplo: "nombre@ejemplo.com", falta: "Escribe el correo del cliente" },
  // Encuentra al titular y al conductor adicional (backend/privacy/export.py).
  dni: { nombre: "DNI, NIE o pasaporte", ejemplo: "12345678Z", falta: "Escribe el DNI, NIE o pasaporte" },
};

// Plazos de conservación de la instalación, exportación de los datos de una persona y registro de accesos a datos
// personales del panel (backend/privacy).
export default function PrivacySettings() {
  const [d, setD] = useState(null);
  // Lo guardado (lo último que ha devuelto el backend), sin lo que se está escribiendo.
  const [guardado, setGuardado] = useState(null);
  const [saving, setSaving] = useState(false);
  const [politicaPropia, setPoliticaPropia] = useState(false);
  const [busqueda, setBusqueda] = useState({ tipo: "email", valor: "" });
  const [exportando, setExportando] = useState(false);
  // 409 de la exportación (el correo lo usan varias personas): se queda a la vista hasta el siguiente intento.
  const [avisoExportar, setAvisoExportar] = useState(null);
  const [accesos, setAccesos] = useState(null);
  const [errorAccesos, setErrorAccesos] = useState(null);
  const [filtro, setFiltro] = useState("");
  // Mientras se lee, el filtro espera; y solo cuenta la última lectura (una respuesta lenta no pisa a otra).
  const [cargandoAccesos, setCargandoAccesos] = useState(true);
  const lecturaAccesos = useRef(0);
  // Días que se guarda el registro (ACCESS_LOG_DAYS de la instalación), como lo dice el backend.
  const [plazoAccesos, setPlazoAccesos] = useState(null);

  const cargar = (data) => {
    setD(data);
    setGuardado(data);
  };

  useEffect(() => {
    api.get("/admin/privacy/settings").then(({ data }) => { setD(data); setGuardado(data); })
      .catch((e) => toast.error(formatError(e)));
    // Un texto propio no lleva los plazos de esta pantalla si la empresa no los escribe (o usa sus marcadores). Es
    // un recordatorio: si no se puede leer, la pantalla sigue sin él.
    api.get("/admin/legal").then(({ data }) => setPoliticaPropia(Boolean(data?.privacy_text?.trim()))).catch(() => {});
  }, []);

  // Los 200 accesos más recientes, de todos los usuarios o del que tenga ese correo (el backend no distingue
  // mayúsculas).
  const cargarAccesos = useCallback(async (correo) => {
    const esta = ++lecturaAccesos.current;
    setCargandoAccesos(true);
    try {
      const { data } = await api.get("/admin/privacy/accesses", { params: correo ? { user_email: correo, limit: 200 } : { limit: 200 } });
      if (esta !== lecturaAccesos.current) return;
      setAccesos(Array.isArray(data?.rows) ? data.rows : []);
      setPlazoAccesos(Number.isInteger(data?.dias) ? data.dias : null);
      setErrorAccesos(null);
    } catch (err) {
      if (esta === lecturaAccesos.current) setErrorAccesos(formatError(err));
    } finally {
      if (esta === lecturaAccesos.current) setCargandoAccesos(false);
    }
  }, []);

  useEffect(() => {
    cargarAccesos("");
    return () => { lecturaAccesos.current += 1; }; // al salir, lo que llegue después ya no cuenta
  }, [cargarAccesos]);

  const filtrar = (e) => {
    e.preventDefault();
    if (!cargandoAccesos) cargarAccesos(filtro.trim());
  };

  const save = async () => {
    setSaving(true);
    try {
      const cuerpo = { enabled: d.enabled, export_third_party: d.export_third_party };
      PLAZOS.forEach(({ campo }) => { cuerpo[campo] = Number(d[campo]); });
      const { data } = await api.put("/admin/privacy/settings", cuerpo);
      cargar(data);
      toast.success("Plazos de conservación guardados");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const exportar = async (e) => {
    e.preventDefault();
    const valor = busqueda.valor.trim();
    if (!valor) {
      toast.error(BUSQUEDAS[busqueda.tipo].falta);
      return;
    }
    setExportando(true);
    setAvisoExportar(null);
    try {
      const resp = await api.get("/admin/privacy/export", { params: { [busqueda.tipo]: valor }, responseType: "blob" });
      descargarBlob(resp, "datos.zip");
      toast.success("Exportación descargada");
    } catch (err) {
      const texto = (await errorDeBlob(err)) || formatError(err);
      if (err?.response?.status === 409) setAvisoExportar(texto);
      else toast.error(texto);
    } finally {
      setExportando(false);
    }
  };

  const aviso = avisoPlazos(d);
  const porCorregir = ajustesPorCorregir(guardado);
  const buscada = BUSQUEDAS[busqueda.tipo];
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca"><ArrowLeft className="h-4 w-4 mr-2" /> Panel</Link>
        </div>
      </header>
      <main className="container-x py-8 max-w-3xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A]">Protección de datos</h1>
          <Button onClick={save} disabled={saving || !d} className="bg-marca hover:bg-marca-hover text-white" data-testid="privacy-save">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
        {d && (
          <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 space-y-4" data-testid="privacy-retention">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="font-display text-lg font-semibold text-[#1A1A1A]">Conservación de datos</h2>
                <p className="text-sm text-[#5C5C5C]">Cada noche se suprime lo que ha cumplido su plazo. Estos plazos salen en tu política de privacidad.</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Label htmlFor="privacy-enabled">Supresión automática</Label>
                <Switch id="privacy-enabled" checked={Boolean(d.enabled)} onCheckedChange={(v) => setD((x) => ({ ...x, enabled: v }))} data-testid="privacy-enabled" />
              </div>
            </div>
            {porCorregir && (
              <div role="alert" className="rounded-xl border border-[#B54434]/40 bg-[#B54434]/10 p-4 text-sm text-[#7A2A1F]"
                   data-testid="privacy-invalid-settings">
                No se ha suprimido nada: los plazos guardados no son válidos
                {porCorregir.length > 0 ? ` (${porCorregir.join(", ")})` : ""}. Corrígelos y pulsa «Guardar».
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              {PLAZOS.map(({ campo, titulo, unidad, min, max }) => (
                <div key={campo}>
                  <Label htmlFor={campo}>{titulo}</Label>
                  <Input id={campo} type="number" min={min} max={max} value={d[campo]} disabled={!d.enabled}
                         onChange={(e) => setD((x) => ({ ...x, [campo]: e.target.value }))} className={inputCls}
                         data-testid={`privacy-${campo}`} />
                  <p className="text-xs text-[#8A8A8A] mt-1">{unidad}</p>
                </div>
              ))}
            </div>
            {d.enabled && (
              <p className="text-sm text-[#5C5C5C]" data-testid="privacy-registry-note">
                Los datos del registro de viajeros se suprimen a los 3 años del fin del alquiler (plazo legal, no ajustable).
              </p>
            )}
            <div>
              <Label htmlFor="export_third_party">Al exportar los datos de un cliente, de la otra persona de la reserva (el conductor adicional, o al revés) se incluye:</Label>
              <select id="export_third_party" value={d.export_third_party}
                      onChange={(e) => setD((x) => ({ ...x, export_third_party: e.target.value }))}
                      className="mt-1 h-10 w-full rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-3 text-sm" data-testid="privacy-third-party">
                <option value="nombre">Solo el nombre</option>
                <option value="todo">Todos sus datos y fotos</option>
              </select>
            </div>
            {aviso && <div role="status" className="rounded-xl border border-[#E89B33]/40 bg-[#E89B33]/10 p-4 text-sm text-[#7A4E0E]" data-testid="privacy-warning">{aviso}</div>}
            {politicaPropia && d.enabled === true && (
              <div role="status" className="rounded-xl border border-[#E89B33]/40 bg-[#E89B33]/10 p-4 text-sm text-[#7A4E0E]" data-testid="privacy-own-policy">
                Tu política de privacidad es un texto propio: revisa que diga los plazos de conservación de esta pantalla.{" "}
                <Link to="/admin/textos-legales" className="underline">Abrir Privacidad y aviso legal</Link>
              </div>
            )}
            <p className="text-sm text-[#5C5C5C]" data-testid="privacy-last-run"><strong>Última purga:</strong> {resumenPurga(d.last_run)}</p>
            <p className="text-xs text-[#8A8A8A]">Los plazos por defecto están pendientes de revisión legal. Una reserva comunicada a las autoridades se conserva el plazo que fija la ley.</p>
          </section>
        )}
        <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 space-y-3">
          <h2 className="font-display text-lg font-semibold text-[#1A1A1A]">Exportar los datos de una persona</h2>
          <p className="text-sm text-[#5C5C5C]">Para atender el derecho de acceso o de portabilidad: un ZIP con sus reservas, correos, pagos, contratos y fotos. No se guarda en el servidor.</p>
          <form onSubmit={exportar} className="flex flex-wrap items-end gap-2" data-testid="privacy-export-form">
            <select value={busqueda.tipo} onChange={(e) => setBusqueda((b) => ({ ...b, tipo: e.target.value }))} aria-label="Buscar por"
                    className="h-10 rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-3 text-sm" data-testid="privacy-export-type">
              {Object.entries(BUSQUEDAS).map(([tipo, { nombre }]) => <option key={tipo} value={tipo}>{nombre}</option>)}
            </select>
            <Input value={busqueda.valor} onChange={(e) => setBusqueda((b) => ({ ...b, valor: e.target.value }))}
                   aria-label={buscada.nombre} placeholder={buscada.ejemplo}
                   className="flex-1 min-w-[220px] bg-[#F7F5F0] border-[#DCD8D0]" data-testid="privacy-export-value" />
            <Button type="submit" variant="outline" disabled={exportando} data-testid="privacy-export-btn">
              <Download className="h-4 w-4 mr-2" /> {exportando ? "Preparando…" : "Exportar"}
            </Button>
          </form>
          {avisoExportar && (
            <div role="alert" className="rounded-xl border border-[#B54434]/40 bg-[#B54434]/10 p-4 text-sm text-[#7A2A1F]" data-testid="privacy-export-warning">
              {avisoExportar}
            </div>
          )}
        </section>
        <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 space-y-3" data-testid="privacy-accesses">
          <h2 className="font-display text-lg font-semibold text-[#1A1A1A]">Registro de accesos</h2>
          <p className="text-sm text-[#5C5C5C]">
            Quién del panel ha visto, exportado, cambiado o borrado datos de los clientes, y cuándo.
            {plazoAccesos ? ` Se guarda ${cuantos(plazoAccesos, "día")}.` : ""}
            {" "}Los accesos del personal de Avant se apuntan aparte, en el servidor.
          </p>
          <form onSubmit={filtrar} className="flex flex-wrap items-end gap-2" data-testid="privacy-accesses-form">
            <Input value={filtro} onChange={(e) => setFiltro(e.target.value)} aria-label="Correo del usuario del panel"
                   placeholder="Correo del usuario (vacío: todos)" inputMode="email"
                   className="flex-1 min-w-[220px] bg-[#F7F5F0] border-[#DCD8D0]" data-testid="privacy-accesses-filter" />
            <Button type="submit" variant="outline" disabled={cargandoAccesos} data-testid="privacy-accesses-btn">
              {cargandoAccesos ? "Cargando…" : "Filtrar"}
            </Button>
          </form>
          {errorAccesos ? (
            <p role="alert" className="text-sm text-[#B54434]" data-testid="privacy-accesses-error">
              No se ha podido leer el registro de accesos: {errorAccesos}
            </p>
          ) : accesos && (
            <ul className="text-sm text-[#1A1A1A] divide-y divide-[#EFECE6]" data-testid="privacy-accesses-list">
              {!accesos.length && <li className="py-2 text-[#5C5C5C]">Sin accesos apuntados</li>}
              {accesos.map((f, i) => (
                <li key={`${f.at}-${i}`} className="py-2 flex flex-wrap items-center justify-between gap-2">
                  <span>{lineaAcceso(f)}</span>
                  {/* Una reserva borrada ya no tiene ficha. */}
                  {f.reservation_id && f.accion !== "borrar_reserva" && (
                    <Link to={`/admin/reservations/${f.reservation_id}`} className="text-xs text-marca underline">Ver reserva</Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
